import {
    BaseFeatureArgs,
    FixedFilterGenerator,
    InitialQueryState,
    QueryManagerOptions,
    SkipOrArgs,
} from '@common/types/queryState';
import { isEmpty } from '@common/utils/typeGuards';
import { useClips, UseClipsArgs } from '@features/Clip';
import { skipToken } from '@reduxjs/toolkit/query';
import { useMemo } from 'react';
import { useCollectionAssetIds } from './useCollectionAssetIds';
import { composeGenerators } from '@common/utils/query/composeGenerators';

export interface UseCollectionAssetsArgs extends BaseFeatureArgs {
    collectionId: string;
}

const collectionAssetsFilterGenerator: FixedFilterGenerator<
    BaseFeatureArgs & { assetIds: string[] }
> = (args) => {
    return [
        `{!tag=clipOrSubclipHasAttachedEditorialObjectId}(clipOrSubclipHasAttachedEditorialObjectId:(${args.assetIds.map((id) => `"${id}"`).join(' ')}))`,
        'funnel:(MediaObjectAnnotation ReviewComment ClipAnnotation TranscriptAnnotation Chapter) -render_originalMediaObjectId:[* TO *]',
    ];
};

export const useCollectionAssets = (
    initialArgs: InitialQueryState = {},
    args: SkipOrArgs<UseCollectionAssetsArgs>,
    options?: QueryManagerOptions,
) => {
    const eoArgs = useMemo(() => {
        if (args === skipToken) return skipToken;
        const {
            fixedFilterGenerators: _ignoreFixed,
            overrideFilterGenerator: _ignoreOverride,
            ...cleanArgs
        } = args;

        return cleanArgs;
    }, [args]);

    const { data: assetIdsResponse, isLoading: isAssetIdsLoading } =
        useCollectionAssetIds(eoArgs);

    const assetIds: string[] = assetIdsResponse?.map((asset) => asset.id);

    const queryArgs: SkipOrArgs<UseClipsArgs> = useMemo(() => {
        if (args === skipToken || isAssetIdsLoading || isEmpty(assetIds))
            return skipToken;

        const mergedGenerators = composeGenerators(
            collectionAssetsFilterGenerator,
            args.fixedFilterGenerators,
        );

        return {
            ...args,
            assetIds,
            fixedFilterGenerators: mergedGenerators,
        } as UseClipsArgs;
    }, [args, assetIds, isAssetIdsLoading]);

    const { isLoading: isAssetsLoading, ...queryResult } = useClips(
        initialArgs,
        queryArgs,
        options,
    );

    return {
        ...queryResult,
        isLoading: isAssetIdsLoading || isAssetsLoading,
    };
};
